/**
 * useEmployees Hook
 * Custom hook for managing employee list with filters, pagination and sorting
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  Employee, 
  EmployeeListParams, 
  EmployeeFilters 
} from '../types/employee';
import { PaginatedResponse } from '../types/common';
import employeeService from '../services/employeeService';
import { useSnackbar } from 'notistack';

interface UseEmployeesResult {
  employees: Employee[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  filters: EmployeeFilters;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setFilters: (filters: EmployeeFilters) => void;
  setSorting: (sortBy: string, sortOrder: 'ASC' | 'DESC') => void;
  refresh: () => void;
  deleteEmployee: (id: number) => Promise<boolean>;
}

export const useEmployees = (initialParams?: EmployeeListParams): UseEmployeesResult => {
  const { enqueueSnackbar } = useSnackbar();
  
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const [page, setPage] = useState(initialParams?.page ?? 1);
  const [limit, setLimit] = useState(initialParams?.limit ?? 20);
  const [filters, setFilters] = useState<EmployeeFilters>(initialParams?.filters ?? {});
  const [sortBy, setSortBy] = useState(initialParams?.sortBy || 'name');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(initialParams?.sortOrder || 'ASC');
  
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  });

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: EmployeeListParams = {
        page,
        limit,
        sortBy,
        sortOrder,
        filters
      };
      
      console.log('📡 [useEmployees] Fetching employees with params:', params);
      const response: PaginatedResponse<Employee> = await employeeService.getEmployees(params);
      console.log('📦 [useEmployees] Response received:', response);
      console.log('👥 [useEmployees] Employees count:', response.data?.length || 0);
      console.log('📊 [useEmployees] Pagination:', response.pagination);
      
      if (response.data && response.data.length > 0) {
        console.log('👤 [useEmployees] First employee:', response.data[0]);
      }
      
      setEmployees(response.data);
      setPagination(response.pagination);
    } catch (err: any) {
      console.error('❌ [useEmployees] Error fetching employees:', err);
      const errorMessage = err.response?.data?.message || 'Erro ao carregar funcionários';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortBy, sortOrder, filters, enqueueSnackbar]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleSetPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSetLimit = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page when changing limit
  }, []);

  const handleSetFilters = useCallback((newFilters: EmployeeFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filtering
  }, []);

  const handleSetSorting = useCallback((newSortBy: string, newSortOrder: 'ASC' | 'DESC') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  }, []);

  const refresh = useCallback(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const deleteEmployee = useCallback(async (id: number): Promise<boolean> => {
    try {
      await employeeService.deleteEmployee(id);
      enqueueSnackbar('Funcionário excluído com sucesso', { variant: 'success' });
      refresh();
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao excluir funcionário';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return false;
    }
  }, [enqueueSnackbar, refresh]);

  return {
    employees,
    loading,
    error,
    pagination,
    filters,
    sortBy,
    sortOrder,
    setPage: handleSetPage,
    setLimit: handleSetLimit,
    setFilters: handleSetFilters,
    setSorting: handleSetSorting,
    refresh,
    deleteEmployee
  };
};
